import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import MainContainer from "../../layout/MainContainer";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import Loader from "../../constants/Loader";

import { isString, isValidInput } from "../../utils/validations";
import {
  useAddReligionMutation,
  useLazyReligionDetailsQuery,
  useUpdateReligionMutation,
} from "../../service/religion";

const AddReligion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [getReligionDetails] = useLazyReligionDetailsQuery();
  const [addReligion] = useAddReligionMutation();
  const [updateReligion] = useUpdateReligionMutation();

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await getReligionDetails({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        formik.setFieldValue("religion", response?.data?.religion);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      religion: "",
    },

    validationSchema: Yup.object({
      religion: Yup.string()
        .required("Country name is required")
        .min(2, "Minimum 2 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        religion: values.religion,
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateReligion({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addReligion(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Religion name updated successfully");
          } else {
            successToast("Religion name added successfully");
          }
          navigate("/manage-religions");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Religions</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-religions")}>Religions</span>{" "}
            - {id ? "Edit" : "Add"} Religion
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Religion Name</label>
              <TextField
                variant="outlined"
                placeholder="Religion Name"
                fullWidth
                hiddenLabel
                className="text_field"
                name="religion"
                id="religion"
                inputProps={{ maxLength: 40 }}
                value={formik.values.religion}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.religion && formik.errors.religion}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddReligion;
